import { CheckCircleOutlineRounded, HighlightOffRounded, SendRounded } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { FormTextField } from "../../../../../components/fields";
import { ReactQueryKey } from "../../../../../types";
import { stepState } from "../../../recoil";
import { IFormValues, InsertBookingStep } from "../../../types";
import { useCheckAffiliated } from "../../../../../api/services";

const FormAffiliationCode = () => {

    const { t } = useTranslation('booking');

    const step = useRecoilValue(stepState);

    const formContext = useFormContext<IFormValues>();
    const affiliationCode = useWatch<IFormValues, 'extraServices.affiliationCode'>({
        name: 'extraServices.affiliationCode'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const queryClient = useQueryClient();

    const checkAffiliationQuery = useCheckAffiliated(
        {genVendorCode: affiliationCode, tenant: tenant as string, dealer: dealer as string},
        {
            enabled: false,
            onSuccess: async (data) => {
                if (!data?.valid) {
                    formContext.setError('extraServices.affiliationCode', {
                        message: 'message.validation.invalid_code'
                    });
                    return;
                }
                formContext.clearErrors('extraServices.affiliationCode');
                formContext.resetField('extraServices.discountOverprice');
                formContext.setValue('extraServices.checkAffiliated', data);
            },
            onError: () => {
                formContext.setError('extraServices.affiliationCode', {
                    message: 'message.validation.invalid_code'
                });
            }
        }
    );

    const handleCheck = useCallback(() => {
        checkAffiliationQuery.refetch();
    }, []);
    const handleKeyDown = useCallback(() => {
        queryClient.resetQueries([ReactQueryKey.AffiliationCode, {tenant: tenant as string, dealer: dealer as string}]);
        if (formContext.getFieldState('extraServices.affiliationCode').error) {
            formContext.clearErrors('extraServices.affiliationCode');
        }
    }, []);

    const EndAdornemnt = useMemo(() => {
        if (
            !affiliationCode
        ) {
            return undefined;
        }
        if (checkAffiliationQuery.isFetching) {
            return <CircularProgress size={20} />
        }
        if (!checkAffiliationQuery.data) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={handleCheck}
                        disabled={step === InsertBookingStep.CUSTOMER_DETAILS}
                    >
                        <SendRounded />
                    </IconButton>
                </InputAdornment>
            )
        }
        if (!checkAffiliationQuery.data.valid) {
            return (
                <HighlightOffRounded
                    sx={{
                        fill: 'red'
                    }}
                />
            );
        }
        return (
            <CheckCircleOutlineRounded
                sx={{
                    fill: 'green'
                }}
            />
        );
    }, [
        checkAffiliationQuery,
        affiliationCode,
        step
    ]);

    return (
        <FormTextField<IFormValues>
            name="extraServices.affiliationCode"
            label={t('label.affiliationCode')}
            disabled={checkAffiliationQuery.isFetching || step === InsertBookingStep.CUSTOMER_DETAILS}
            InputProps={{
                endAdornment: EndAdornemnt
            }}
            onKeyDown={handleKeyDown}
        />
    );
}

export default FormAffiliationCode;
